import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {
  AuditTrailPanelId,
  makeCellsPanelId,
  makeViewerPanelId,
} from '../../libs/shared/core/src/store/panels/panelStateBase';
import {AuditTrailFiltersSelection} from '../store/panels/audit_trail/auditTrailParameters';
import {InboxesAuditTrailParameters} from '../store/panels/inboxes/parameters/inboxesParameters';
import {AuditTrailCellsContainer} from './cells/auditTrailCellsContainer';
import {AuditTrailFiltersMenuContainer} from './filters_menu/auditTrailFiltersMenuContainer';
import {AuditTrailViewerContainer} from './viewer/auditTrailViewerContainer';

/*
 * Props.
 */

interface AuditTrailProps {
  panelId: AuditTrailPanelId;
  /** Whether this component is visible on screen. Used for Apple Handoff. */
  isActive: boolean;
  parameters: InboxesAuditTrailParameters;
  onParametersChange: (parameters: InboxesAuditTrailParameters) => void;
}

/*
 * Style.
 */

const StyledAuditTrailerContainerDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr;
  grid-template-rows: 100%;
  height: 100%;
  position: relative;
`;

// Have to mimic grid css from inboxes view
const StyledViewerContainerDiv = styled.div`
  display: grid;
  grid-template-rows: min-content min-content minmax(0, 1fr);
  grid-template-columns: auto auto minmax(540px, 1fr) auto;
`;

/*
 * Component.
 */

export const AuditTrail: FC<AuditTrailProps> = (props) => {
  const {panelId, parameters, onParametersChange} = props;

  const cellsPanelId = makeCellsPanelId(panelId);
  const viewerPanelId = makeViewerPanelId(panelId);

  const buildFiltersParameters = useCallback(
    (filters: AuditTrailFiltersSelection): InboxesAuditTrailParameters => ({
      ...parameters,
      filters,
    }),
    [parameters],
  );

  const onFiltersChange: (selection: AuditTrailFiltersSelection) => void = useCallback(
    (selection) => onParametersChange(buildFiltersParameters(selection)),
    [buildFiltersParameters, onParametersChange],
  );

  return (
    <StyledAuditTrailerContainerDiv>
      {/* P1 menu */}
      <AuditTrailFiltersMenuContainer
        panelId={panelId}
        filters={parameters.filters}
        onFiltersChange={onFiltersChange}
      />

      {/* P2 & P3 in one main grid component */}
      <StyledViewerContainerDiv>
        {/* P2 cells */}
        <AuditTrailCellsContainer panelId={cellsPanelId} onParametersChange={onParametersChange} />

        {/* P3 Viewer */}
        <AuditTrailViewerContainer
          panelId={viewerPanelId}
          cellsPanelId={cellsPanelId}
          isActive={props.isActive}
        />
      </StyledViewerContainerDiv>
    </StyledAuditTrailerContainerDiv>
  );
};
